// Fonctionnalités — page publique (indexable).

import { Link } from "react-router-dom";
import { ArrowRight, GitBranch, Lock, Camera, FileText, LayoutDashboard, MessageSquare, Bell, ShieldCheck } from "lucide-react";
import SEOHead from "../SEOHead";
import { seoConfig, jsonLdBreadcrumb } from "../seoConfig";

const P = seoConfig.pages.fonctionnalites;

const Feature = ({ icon: Icon, title, body, testId }) => (
  <article
    data-testid={testId}
    className="rounded-2xl border border-black/5 p-8 bg-white hover:bg-[#F7F8FA] transition-colors"
  >
    <div className="h-10 w-10 rounded-lg bg-[#F7F8FA] flex items-center justify-center text-[#111]">
      <Icon size={18} strokeWidth={2} />
    </div>
    <h3 className="mt-6 text-[17px] font-semibold text-[#111]">{title}</h3>
    <p className="mt-2 text-[14px] text-[#6B7280] leading-relaxed">{body}</p>
  </article>
);

const Fonctionnalites = () => {
  return (
    <>
      <SEOHead
        title={P.title}
        description={P.description}
        keywords={P.keywords}
        path={P.path}
        jsonLd={jsonLdBreadcrumb([
          { name: "Accueil", path: "/" },
          { name: "Fonctionnalités", path: P.path },
        ])}
      />

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 pt-20 pb-16 lg:pt-28">
        <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
          Fonctionnalités
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-[#111] max-w-3xl">
          Tout le suivi de votre relation client,<br />en un seul outil.
        </h1>
        <p className="mt-6 text-[15px] lg:text-[16px] leading-relaxed text-[#6B7280] max-w-2xl">
          WYVIO couvre l&apos;intégralité du cycle opérationnel — de la
          signature du bon de commande à la remise du véhicule — sans se substituer
          à vos outils existants.
        </p>
      </section>

      {/* Grid des fonctionnalités */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Feature
            testId="feat-workflows"
            icon={GitBranch}
            title="Workflows configurables"
            body="Chaque équipe définit ses propres étapes. Ajoutez, réordonnez, supprimez des étapes sans code."
          />
          <Feature
            testId="feat-blocking"
            icon={Lock}
            title="Prérequis de livraison"
            body="Les étapes marquées comme bloquantes empêchent la confirmation de livraison tant qu'elles ne sont pas complétées. Une seule règle, jamais oubliée."
          />
          <Feature
            testId="feat-media"
            icon={Camera}
            title="Media Hub unifié"
            body="Documents, photos et vidéos rassemblés au même endroit. Versionning, archivage sans suppression, aperçu instantané dans le navigateur."
          />
          <Feature
            testId="feat-docs"
            icon={FileText}
            title="Documents traçables"
            body="Chaque document est lié à une étape, horodaté, et son ouverture par le client est enregistrée. Aucun doute sur ce qui a été transmis, ni quand."
          />
          <Feature
            testId="feat-comms"
            icon={MessageSquare}
            title="Communications centralisées"
            body="Emails et SMS automatiques ou manuels envoyés depuis l'application. L'historique complet reste attaché au dossier."
          />
          <Feature
            testId="feat-notif"
            icon={Bell}
            title="Rappels intelligents"
            body="Paiements en attente, livraisons à venir, documents manquants : chaque relance passe par le moteur de communication avec idempotency."
          />
          <Feature
            testId="feat-portal"
            icon={ShieldCheck}
            title="Portail client sécurisé"
            body="Lien magique 24h à usage unique, ou mot de passe. Le client voit uniquement ce que l'équipe a choisi de rendre visible."
          />
          <Feature
            testId="feat-dashboard"
            icon={LayoutDashboard}
            title="Centre de pilotage équipe"
            body="Livraisons du jour, dossiers bloqués, dossiers prêts, actions prioritaires : une vue unique pour toute l'équipe."
          />
          <Feature
            testId="feat-payment"
            icon={ShieldCheck}
            title="Suivi des paiements"
            body="Confirmation en un clic côté équipe, déclaration côté client, rappels programmables. La livraison reste bloquée tant que le paiement n'est pas validé."
          />
        </div>
      </section>

      {/* Deep-dive: workflows */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 border-t border-black/5">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">Workflows</p>
            <h2 className="mt-3 text-3xl font-semibold text-[#111] tracking-[-0.02em] max-w-md">
              Chaque équipe a ses propres règles.<br />WYVIO les respecte.
            </h2>
            <p className="mt-5 text-[15px] text-[#6B7280] leading-relaxed">
              Les workflows sont définis dans les paramètres : une étape peut être
              obligatoire, facultative, visible ou non par le client. Elle peut
              exiger la présence d'un document, ou bloquer la livraison tant qu'elle
              n'est pas complétée.
            </p>
            <ul className="mt-8 space-y-3 text-[14px] text-[#111]">
              <li className="flex gap-3"><span className="text-[#8FB600]">→</span> Étapes réordonnables, duplicables, archivables</li>
              <li className="flex gap-3"><span className="text-[#8FB600]">→</span> Snapshots immuables : modifier un workflow n'impacte pas les dossiers en cours</li>
              <li className="flex gap-3"><span className="text-[#8FB600]">→</span> Versionning automatique à chaque changement</li>
            </ul>
          </div>
          <div className="rounded-2xl bg-[#111] text-white p-8">
            <p className="text-[11px] uppercase tracking-widest text-white/50">Aperçu — Workflow « Vente directe »</p>
            <div className="mt-5 space-y-2">
              {[
                "Réservation",
                "Acompte reçu",
                "Dossier administratif",
                "Contrôle technique",
                "Préparation esthétique",
                "Immatriculation",
                "Solde payé",
                "Livraison programmée",
                "CPI remis",
              ].map((l, i) => (
                <div key={l} className="flex items-center gap-3 rounded-lg bg-white/[0.04] px-3.5 py-2">
                  <span className="text-[11px] text-white/40 w-4">{i + 1}</span>
                  <p className="text-[13.5px] text-white/90">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 py-24">
        <div className="rounded-3xl bg-[#F7F8FA] p-12 lg:p-16 border border-black/5">
          <h2 className="text-3xl font-semibold text-[#111] tracking-[-0.02em] max-w-xl">
            Une démonstration vaut mieux qu'une page marketing.
          </h2>
          <p className="mt-4 text-[15px] text-[#6B7280] max-w-xl">
            Écrivez-nous et nous vous montrerons WYVIO appliqué à un cas concret,
            avec vos étapes et votre vocabulaire.
          </p>
          <Link
            to="/contact"
            data-testid="features-cta"
            className="mt-8 h-12 inline-flex items-center gap-2 rounded-full bg-[#111] text-white text-[14px] font-medium px-6 hover:bg-black transition"
          >
            Prendre contact
            <ArrowRight size={16} strokeWidth={2.25} />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Fonctionnalites;
